import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../../../component/ui/Button';
import DataNotFound from '../../components/dataNotFound';
import CreateNewListModal from './components/CreateNewListModal';
import Table from '../../../component/table/Table';
import Pagination from '../../../component/pagination';
import { getListTableColumns } from './components/ListTableColumn';
import sortbydot from '../../../assets/image/sort_by_group.svg';
import { toast } from 'react-toastify';
import './List.scss';

// Temporary Dummy Data
const initialDummyRows = [
	{
		id: 1,
		'LIST NAME': 'Creative Concoctions',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 200,
	},
	{
		id: 2,
		'LIST NAME': 'Kristin Watson',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 150,
	},
	{
		id: 3,
		'LIST NAME': 'Kathryn Murphy',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 100,
	},
	{
		id: 4,
		'LIST NAME': 'Robert Fox',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 60,
	},
	{
		id: 5,
		'LIST NAME': 'Berkay Erdinc',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 1000,
	},
	{
		id: 6,
		'LIST NAME': 'Bessie Cooper',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 800,
	},
	{
		id: 7,
		'LIST NAME': 'Darrell Steward',
		DESCRIPTION: 'Lorem Ipsum dummy text',
		'CREATED DATE': '18/05/2024',
		'PRODUCT COUNT': 950,
	},
];

const ProductLists = () => {
	const [searchParams] = useSearchParams();
	const [headerDropDown, setHeaderDropDown] = useState('All Lists');
	const [row, setRow] = useState(initialDummyRows);
	const [loading] = useState(false);
	const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
	const [selectedRows, setSelectedRows] = useState([]);
	const [sortDirection, setSortDirection] = useState({ 'LIST NAME': 'asc' });

	const [params, setParams] = useState(() => ({
		page: Number(searchParams.get('page')) || 1,
		pageSize: Number(searchParams.get('pageSize')) || 20,
	}));

	const totalPages = Math.ceil(row.length / params.pageSize) || 1;

	const handleSaveSuccess = (newListData) => {
		setRow((prev) => [
			{
				id: Date.now(),
				'LIST NAME': newListData.name,
				DESCRIPTION: newListData.description,
				'CREATED DATE': newListData.created_date || '01/09/2026',
				'PRODUCT COUNT': newListData.product_count || 0,
			},
			...prev,
		]);
	};

	const handleDeleteSelected = () => {
		if (!selectedRows || selectedRows.length === 0) {
			toast.error('Please select at least one list to delete');
			return;
		}

		setRow((prev) => prev.filter((item) => !selectedRows.includes(item.id)));
		setSelectedRows([]);
		toast.success('list  deleted successfully');
	};

	const handleSort = (fieldKey) => {
		const currentSort = sortDirection[fieldKey] === 'asc' ? 'desc' : 'asc';
		setSortDirection({ [fieldKey]: currentSort });

		setRow((prevRows) => {
			return [...prevRows].sort((a, b) => {
				let valA = a[fieldKey] ?? '';
				let valB = b[fieldKey] ?? '';

				if (typeof valA === 'number' && typeof valB === 'number') {
					return currentSort === 'asc' ? valA - valB : valB - valA;
				}

				valA = String(valA).toLowerCase();
				valB = String(valB).toLowerCase();

				if (valA < valB) return currentSort === 'asc' ? -1 : 1;
				if (valA > valB) return currentSort === 'asc' ? 1 : -1;
				return 0;
			});
		});
	};

	const columns = useMemo(
		() => getListTableColumns(handleSort, sortDirection),
		[sortDirection]
	);

	return (
		<div style={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
			{/* Top Header Bar */}
			<div className="product-pg-header">
				<div className="d-flex justify-content-start align-items-center gap-4">
					<div className="dropdown text-end">
						<a
							href="#"
							className="d-block link-dark text-decoration-none dropdown-toggle"
							id="dropdownUser1"
							data-bs-toggle="dropdown"
							aria-expanded="false"
						>
							<p>{headerDropDown}</p>
						</a>
						<ul
							className="dropdown-menu font-14"
							aria-labelledby="dropdownUser1"
							style={{ minWidth: '240px' }}
						>
							{['All Lists', 'Active Lists', 'Inactive Lists'].map((item) => (
								<li key={item}>
									<a className="dropdown-item" onClick={() => setHeaderDropDown(item)}>
										{item}
									</a>
								</li>
							))}
						</ul>
					</div>
				</div>

				<div className="d-flex align-items-center gap-3">
					{/* Delete Button */}
					<Button
						type="button"
						button="Delete"
						className="btn-gray-header mt-0 px-3"
						onClick={handleDeleteSelected}
					/>

					{/* Export Button */}
					<Button
						type="button"
						button="Export"
						className="btn-gray-header mt-0 px-3"
					/>

					{/* Create New List Button */}
					<Button
						add
						type="button"
						button="Create New List"
						className="btn-primary mt-0 px-4"
						onClick={() => setIsOpenCreateModal(true)}
					/>

					{/* 3-Dots Menu */}
					<div className="dropdown text-end ms-2">
						<a
							href="#"
							className="d-block link-dark text-decoration-none"
							id="dropdownUser1"
							data-bs-toggle="dropdown"
							aria-expanded="false"
						>
							<img src={sortbydot} alt="arrow" />
						</a>
						<ul
							className="dropdown-menu font-14"
							aria-labelledby="dropdownUser1"
							style={{ minWidth: '240px' }}
						>
							<li><a className="dropdown-item sortBy">Sort By</a></li>
							<li><a className="dropdown-item">Created Time</a></li>
							<li><a className="dropdown-item">Name</a></li>
							<hr />
							<li><a className="dropdown-item">Import Lists</a></li>
							<li><a className="dropdown-item">Export Lists</a></li>
							<hr />
							<li><a className="dropdown-item">Preferences</a></li>
							<li><a className="dropdown-item">Refresh</a></li>
						</ul>
					</div>
				</div>
			</div>

			{/* Main Content Area */}
			{loading ? (
				<div className="d-flex justify-content-center align-items-center flex-grow-1">
					<span className="Page_loader" role="status" aria-hidden="true"></span>
				</div>
			) : row.length > 0 ? (
				<>
					<div className="productTable-body">
						<Table
							row={row}
							coloum={columns}
							className="productTable"
							action={setSelectedRows}
							style={{ height: 'calc(100vh - 148px)' }}
						/>
					</div>
					<Pagination
						currentPage={params.page}
						totalPages={totalPages}
						onChangePage={(newPage) => setParams((prev) => ({ ...prev, page: newPage }))}
						pageSize={params.pageSize}
						setParam={(value) => setParams((prev) => ({ ...prev, pageSize: value, page: 1 }))}
					/>
				</>
			) : (
				<DataNotFound
					title={'Organize and manage your products!'}
					subtitle={'Manage all your product groups easily.'}
					descriptionTitle={'Keep your inventory organized!'}
					descriptionSubTitle={'Manage product details, pricing, stock and more — all in one place.'}
					buttonText={'Add New List'}
					onClick={() => setIsOpenCreateModal(true)}
				/>
			)}

			{/* Create New List Modal */}
			{isOpenCreateModal && (
				<CreateNewListModal
					isOpen={isOpenCreateModal}
					onClose={() => setIsOpenCreateModal(false)}
					onSaveSuccess={handleSaveSuccess}
				/>
			)}
		</div>
	);
};

export default ProductLists;
